import React, { useContext, useCallback, useMemo } from "react";
import InputField from "./input.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import MapDialog from "../Map/MapDialog.js";
import AddressSearchContextProvider from "../../context/addressSearchContext.js";
import { AddressSearchContext } from "../../context/addressSearchContext.js";

// AddressInputContent component - memoized to prevent recreation
const AddressInputContent = React.memo(({
    addressData,
    handleAddressChange,
    handleInputChange,
    error,
    disabled,
    isRequired,
    showRequiredIndicator,
    language,
    label,
    labelFor,
}) => {
    const { selectedAddress } = useContext(AddressSearchContext);

    const {
        country = "Armenia",
        region = "",
        residence = "",
        street = "",
        home = "",
        floor = "",
        entrance = "",
        flat = "",
        domofon = "",
        latitude = null,
        longitude = null,
    } = addressData;

    const handleMapAddressSelect = useCallback((callback) => {
        if (selectedAddress && selectedAddress.location && Array.isArray(selectedAddress.location) && selectedAddress.location.length >= 2) {
            // Only parse Armenian addresses
            const addressLower = selectedAddress.address.toLowerCase();
            const isArmenianAddress = addressLower.includes('armenia') || 
                                     addressLower.includes('երևան') || 
                                     addressLower.includes('yerevan') ||
                                     addressLower.includes('արագածոտն') ||
                                     addressLower.includes('արարատ') ||
                                     addressLower.includes('արմավիր') ||
                                     addressLower.includes('գեղարքունիք') ||
                                     addressLower.includes('կոտայք') ||
                                     addressLower.includes('լոռի') ||
                                     addressLower.includes('շիրակ') ||
                                     addressLower.includes('սյունիք') ||
                                     addressLower.includes('տավուշ') ||
                                     addressLower.includes('վայոց');
            
            // Start with a clean address object, clearing all fields first
            const baseAddress = {
                country: addressData.country || "Armenia", // Keep country
                region: "",
                residence: "",
                street: "",
                home: "",
                floor: "",
                entrance: "",
                flat: "",
                domofon: "",
                coordinates: selectedAddress.location, // [lat, lng]
                latitude: selectedAddress.location[0],
                longitude: selectedAddress.location[1],
            };
            
            // If not an Armenian address, only set coordinates and street
            if (!isArmenianAddress) {
                const updatedAddress = {
                    ...baseAddress,
                    street: selectedAddress.address,
                };
                handleAddressChange(updatedAddress);
                if (callback) callback();
                return;
            }
            
            // Parse address: "Armenia, Yerevan, Moskovyan Street, 8"
            const addressParts = selectedAddress.address.split(',').map(part => part.trim());
            
            // Remove "Armenia" if it's the first part
            const partsWithoutCountry = addressParts[0]?.toLowerCase() === 'armenia' 
                ? addressParts.slice(1) 
                : addressParts;
            
            let parsedRegion = "";
            let parsedResidence = "";
            let parsedStreet = "";
            let parsedHome = "";
            
            if (partsWithoutCountry.length >= 1) {
                // First part is usually the city/region (e.g., "Yerevan")
                // Use it as-is, no mapping or transformation
                const cityName = partsWithoutCountry[0].trim();
                parsedRegion = cityName;
                parsedResidence = cityName;
            }
            
            if (partsWithoutCountry.length >= 2) {
                // Second part is usually the street
                parsedStreet = partsWithoutCountry[1];
            }
            
            if (partsWithoutCountry.length >= 3) {
                // Third part might be building number or additional street info
                const thirdPart = partsWithoutCountry[2].trim();
                // Check if it starts with a digit (building number pattern)
                // Building numbers can be: "23", "23/5", "23-5", "23A", "23/5A", etc.
                if (/^\d/.test(thirdPart)) {
                    // If it starts with a digit, treat the entire part as the building number
                    // This handles formats like "23/5", "23-5", "23A", etc.
                    parsedHome = thirdPart;
                } else {
                    // If not starting with a digit, append to street
                    parsedStreet = parsedStreet ? `${parsedStreet}, ${thirdPart}` : thirdPart;
                }
            }
            
            // If we still don't have a street and there are more parts, use them
            if (!parsedStreet && partsWithoutCountry.length > 1) {
                parsedStreet = partsWithoutCountry.slice(1).join(', ');
            }
            
            // Update address with parsed data (all other fields are already cleared in baseAddress)
            const updatedAddress = {
                ...baseAddress,
                ...(parsedRegion ? { region: parsedRegion } : {}),
                ...(parsedResidence ? { residence: parsedResidence } : {}),
                ...(parsedStreet ? { street: parsedStreet } : {}),
                ...(parsedHome ? { home: parsedHome } : {}),
            };
            
            handleAddressChange(updatedAddress);
            }
            if (callback) callback();
    }, [selectedAddress, addressData, handleAddressChange]);

        return (
            <div className="mb-4">
            {label && (
                <label
                    htmlFor={labelFor}
                    className="block text-sm font-medium text-gray-700 dark:text-[#b1bac4] mb-2"
                >
                    {label}
                    {isRequired && showRequiredIndicator && (
                        <span className="text-red-500 dark:text-red-400 ml-1">*</span>
                    )}
                </label>
            )}

            <div className="space-y-4">
                {/* Country - Fixed to Armenia */}
                <div>
                    <InputField
                        value={country}
                        onChange={() => {}}
                        name="country"
                        label={getTranslation("profile.country", language)}
                        labelFor="address-country"
                        disabled={true}
                        isRequired={false}
                        showRequiredIndicator={false}
                    />
                </div>

                {/* Region (մարզ) */}
                <div>
                    <InputField
                        value={region}
                        onChange={handleInputChange}
                        name="region"
                        label={getTranslation("profile.region", language) || "Մարզ"}
                        labelFor="address-region"
                        placeholder={getTranslation("profile.regionPlaceholder", language) || "Մուտքագրեք մարզ"}
                        isRequired={true}
                        showRequiredIndicator={showRequiredIndicator}
                        error={error?.region}
                        disabled={disabled}
                    />
                </div>

                {/* Residence (Բնակավայր) */}
                <div>
                    <InputField
                        value={residence}
                        onChange={handleInputChange}
                        name="residence"
                        label={getTranslation("profile.residence", language) || "Բնակավայր"}
                        labelFor="address-residence"
                        placeholder={getTranslation("profile.residencePlaceholder", language) || "Մուտքագրեք բնակավայր"}
                        isRequired={true}
                        showRequiredIndicator={showRequiredIndicator}
                        error={error?.residence}
                        disabled={disabled}
                    />
                </div>

                {/* Street with Map Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-[#b1bac4] mb-2">
                        {getTranslation("profile.street", language) || getTranslation("common.street", language)}
                        {showRequiredIndicator && (
                            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
                        )}
                    </label>
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <InputField
                                value={street}
                                onChange={handleInputChange}
                                name="street"
                                label=""
                                labelFor="address-street"
                                placeholder={getTranslation("profile.streetPlaceholder", language) || getTranslation("common.streetPlaceholder", language)}
                                isRequired={false}
                                showRequiredIndicator={false}
                                error={error?.street}
                                disabled={disabled}
                            />
                            {!disabled && (
                                <MapDialog
                                    trigger={
                                        <button
                                            type="button"
                                            className="absolute right-3 top-[1.25rem] -translate-y-1/2 flex items-center justify-center text-customOrange hover:text-orange-600 z-10"
                                            aria-label="Select address on map"
                                        >
                                            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                <circle cx="12" cy="10" r="3"></circle>
                                            </svg>
                                        </button>
                                    }
                                    onApplyAddress={handleMapAddressSelect}
                                    initialCoordinates={
                                        (latitude && longitude) 
                                            ? [latitude, longitude] 
                                            : (addressData.coordinates && Array.isArray(addressData.coordinates) && addressData.coordinates.length >= 2)
                                                ? addressData.coordinates
                                                : null
                                    }
                                />
                            )}
                        </div>
                    </div>
                            {error?.street && (
                        <p className="text-red-500 dark:text-red-400 text-xs mt-1 font-medium">{error.street}</p>
                    )}
                </div>
                
                {/* Coordinates Display (if available) */}
                {(latitude !== null && longitude !== null) && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-[#8b949e]">
                        {getTranslation("profile.coordinates", language) || "Coordinates"}: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </div>
                )}

                {/* Home/Apartment */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <InputField
                            value={home}
                            onChange={handleInputChange}
                            name="home"
                            label={getTranslation("profile.home", language) || getTranslation("common.home", language)}
                            labelFor="address-home"
                            placeholder={getTranslation("profile.homePlaceholder", language) || getTranslation("common.homeNumberPlaceholder", language)}
                            isRequired={false}
                            showRequiredIndicator={showRequiredIndicator}
                            error={error?.home}
                            disabled={disabled}
                        />
                    </div>
                    <div>
                        <InputField
                            value={flat}
                            onChange={handleInputChange}
                            name="flat"
                            label={getTranslation("profile.flat", language) || getTranslation("common.flat", language)}
                            labelFor="address-flat"
                            placeholder={getTranslation("profile.flatPlaceholder", language) || "Մուտքագրեք բնակարան"}
                            isRequired={false}
                            showRequiredIndicator={showRequiredIndicator}
                            error={error?.flat}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {/* Floor and Entrance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <InputField
                            value={floor}
                            onChange={handleInputChange}
                            name="floor"
                            label={getTranslation("profile.floor", language) || getTranslation("common.floor", language)}
                            labelFor="address-floor"
                            placeholder={getTranslation("profile.floorPlaceholder", language) || "Մուտքագրեք հարկ"}
                            isRequired={false}
                            showRequiredIndicator={showRequiredIndicator}
                            error={error?.floor}
                            disabled={disabled}
                        />
                    </div>
                    <div>
                        <InputField
                            value={entrance}
                            onChange={handleInputChange}
                            name="entrance"
                            label={getTranslation("profile.entrance", language) || getTranslation("common.entrance", language)}
                            labelFor="address-entrance"
                            placeholder={getTranslation("profile.entrancePlaceholder", language) || "Մուտքագրեք մուտք"}
                            isRequired={false}
                            showRequiredIndicator={showRequiredIndicator}
                            error={error?.entrance}
                            disabled={disabled}
                        />
                    </div>
                </div>

                {/* Domofon/Intercom */}
                <div>
                    <InputField
                        value={domofon}
                        onChange={handleInputChange}
                        name="domofon"
                        label={getTranslation("profile.domofon", language) || "Դոմոֆոն"}
                        labelFor="address-domofon"
                        placeholder={getTranslation("profile.domofonPlaceholder", language) || "Մուտքագրեք դոմոֆոն"}
                        isRequired={false}
                        showRequiredIndicator={showRequiredIndicator}
                        error={error?.domofon}
                        disabled={disabled}
                    />
                </div>

            </div>

            {error && typeof error === "string" && (
                <p className="text-red-500 dark:text-red-400 text-xs mt-2 font-medium">{error}</p>
            )}
            </div>
        );
});

AddressInputContent.displayName = "AddressInputContent";

const AddressInput = ({
    value = {},
    onChange,
    name,
    label,
    labelFor,
    disabled = false,
    error,
    isRequired = false,
    showRequiredIndicator = true,
}) => {
    const { language } = useLanguage();

    // Address structure - memoized to prevent unnecessary re-renders
    const addressData = useMemo(() => value || {}, [value]);

    const handleAddressChange = useCallback((updatedData) => {
        const syntheticEvent = {
            target: {
                name: name,
                value: updatedData,
            },
        };
        if (onChange) {
            onChange(syntheticEvent);
        }
    }, [name, onChange]);

    const handleFieldChange = useCallback((fieldName, fieldValue) => {
        const updatedAddress = {
            ...addressData,
            [fieldName]: fieldValue,
        };
        handleAddressChange(updatedAddress);
    }, [addressData, handleAddressChange]);

    const handleInputChange = useCallback((e) => {
        const { name: fieldName, value: fieldValue } = e.target;
        handleFieldChange(fieldName, fieldValue);
    }, [handleFieldChange]);

    return (
        <AddressSearchContextProvider>
            <AddressInputContent
                addressData={addressData}
                handleAddressChange={handleAddressChange}
                handleInputChange={handleInputChange}
                error={error}
                disabled={disabled}
                isRequired={isRequired}
                showRequiredIndicator={showRequiredIndicator}
                language={language}
                label={label}
                labelFor={labelFor}
            />
        </AddressSearchContextProvider>
    );
};

export default AddressInput;
